import clsx from "clsx";
import React, { useState } from "react";
import Image from "next/image";
import { BGWrapper } from "../blocks/BGWrapper";
// import { RessessedButton } from "./RessessedButton";
import useWindowResizeThreshold, {
    MOBILE_THRESHOLD,
} from "../header/windowSizeHook";
import { MediaType } from "@/wp-blocks/acf-blocks/extras";
import Link from "next/link";

function getYouTubeId(url: string): string | null {
    const m = url.match(/(?:embed\/|watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
}

interface YouTubeImageListProps {
    panels: { videoUrl: string; title: string; video?: MediaType }[];
    disableBg?: boolean;
    abovePageHeader?: boolean;
    ctaLabel?: string;
    ctaLink?: string;
}

export default function YouTubeImageList({
    panels,
    disableBg,
    abovePageHeader,
    ctaLabel = undefined,
    ctaLink = undefined,
}: YouTubeImageListProps) {
    const isMobile = useWindowResizeThreshold(MOBILE_THRESHOLD);
    const [selectedVideo, setSelectedVideo] = useState(panels[0]);
    const [isPlaying, setIsPlaying] = useState(false);

    const handleSelectPanel = (panel: { videoUrl: string; title: string; video?: MediaType }) => {
        setSelectedVideo(panel);
        setIsPlaying(false);
    };

    const cutoff = isMobile ? 2 : 4;
    const isYouTubeVideo = (url: string) =>
        url.includes("youtube.com") || url.includes("youtu.be");

    if (!panels?.length) return null;
    return (
        <BGWrapper
            ignoretwMerge={true}
            className={clsx(
                !disableBg
                    ? "bg-red-900 bg-[url('/images/wave-patter.webp')] bg-[length:auto_100%] px-4"
                    : "bg-transparent px-4 left-0",
                abovePageHeader && "-mt-16 md:-mt-28",
            )}
        >
            <div className="full-width rounded-2xl max-w-xl">
                <div
                    className={clsx(
                        "border-2 rounded-2xl",
                        !disableBg && "border-white",
                        disableBg && "border-red-700",
                    )}
                >
                    <div
                        className={clsx(
                            "bg-white border-4 border-red-700 rounded-2xl overflow-hidden",
                            disableBg && "border-white",
                        )}
                    >
                        {isYouTubeVideo(selectedVideo.videoUrl) ? (
                            isPlaying ? (
                                <iframe
                                    width="100%"
                                    height="315"
                                    src={`${selectedVideo.videoUrl.replace("watch?v=", "embed/")}?autoplay=1`}
                                    title={selectedVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : (
                                <div
                                    className="relative w-full cursor-pointer"
                                    style={{ height: "315px" }}
                                    onClick={() => setIsPlaying(true)}
                                >
                                    <Image
                                        src={`https://img.youtube.com/vi/${getYouTubeId(selectedVideo.videoUrl)}/hqdefault.jpg`}
                                        alt={selectedVideo.title}
                                        fill
                                        className="object-cover"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                        <svg className="w-16 h-16" viewBox="0 0 68 48" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#ff0000"/>
                                            <path d="M 45,24 27,14 27,34" fill="#ffffff"/>
                                        </svg>
                                    </div>
                                </div>
                            )
                        ) : (
                            <video
                                width="100%"
                                height="315"
                                controls
                                src={selectedVideo.videoUrl}
                                title={selectedVideo.title}
                            />
                        )}
                    </div>
                </div>
                <ul className="flex mx-4">
                    {panels.slice(0, cutoff).map((panel) => (
                        <li
                            key={panel.videoUrl}
                            className={clsx(
                                `rounded-b-2xl relative cursor-pointer w-30 h-8 overflow-hidden text-ellipsis flex-1 flex items-center justify-center leading-0`,
                                selectedVideo.videoUrl === panel.videoUrl
                                    ? disableBg
                                        ? "bg-red-700 text-white"
                                        : "bg-white text-red-700"
                                    : disableBg
                                    ? "bg-transparent text-red-700"
                                    : "bg-transparent text-white",
                            )}
                            onClick={() => handleSelectPanel(panel)}
                        >
                            <span className="mx-3 text-xxs md:text-xxxs text-center text-ellipsis justify-center items-center flex">
                                {panel.title}
                            </span>
                        </li>
                    ))}
                </ul>
                {ctaLabel && ctaLink ? (
                    <div className="flex mt-12 items-center justify-center">
                        <Link
                            href={ctaLink}
                            className="text-red-700 bg-white px-7 py-2 rounded-full cursor-pointer font-archivo text-xxs md:text-xxxs"
                        >
                            {ctaLabel}
                        </Link>
                    </div>
                ) : null}
            </div>
        </BGWrapper>
    );
}

// const RessessedButton: React.FC<
//     React.SVGProps<SVGSVGElement> & { textClassname?: string }
// > = (props) => (
//     <svg
//         xmlns="http://www.w3.org/2000/svg"
//         fill="none"
//         viewBox="0 0 186 47"
//         {...props}
//     >
//         <clipPath id="clip-path">
//             <text
//                 x="50%"
//                 y="50%"
//                 textAnchor="middle"
//                 dominantBaseline="middle"
//                 className={clsx(
//                     "font-bold pt-2 md:pt-0 text-xs md:text-sm",
//                     props.textClassname
//                 )}
//             >
//                 {props.children}
//             </text>
//         </clipPath>
//         <path
//             id="path"
//             startOffset={0}
//             d="M24 23c0 13.255 10.745 24 24 24h90c13.255 0 24-10.745 24-24V0H24zM162 24.24C162 8.242 186 0 186 0h-24zM24 24.24C24 8.242 0 0 0 0h24z"
//         ></path>
//         <use
//             clip-path="url(#clip-path)"
//             href="#path"
//             className={clsx(
//                 "font-bold text-md md:text-sm",
//                 props.textClassname
//             )}
//         />
//     </svg>
// );
